import { useRef, useState } from "react";
import { motion } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  Cog,
  DraftingCompass,
  ExternalLink,
  Hand,
  ListChecks,
  LoaderCircle,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Meh,
  MessageCircle,
  Minus,
  Plus,
  RotateCcw,
  SendHorizonal,
  Settings,
  X,
} from "lucide-react";

import { type Message } from "shared/apiClient";
import { getText } from "shared/lib";
import { type Question, type QuestionData } from "@/components/wizard/lib";
import { animationVariants } from "../lib";

import { useGetObjectsNearAddress } from "shared/apiHooks";
import { cn, createMDWrapper, sentenceCase } from "shared/lib";
import { createMessageEvent, useChannel } from "shared/channel";
import {
  useAccount,
  useCaptureEvent,
  CaptureEventType,
  useCancelOrCompleteApplication,
} from "@/hooks";

import {
  Button,
  Input,
  MapComponent,
  Textarea,
  Toggle,
} from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader";

import "./MultiSelectStep.css";

const iconMap = {
  "arrow-left": <ArrowLeft size={20} />,
  "arrow-right": <ArrowRight size={20} />,
  check: <Check size={20} />,
  "chevron-down": <ChevronDown size={20} />,
  "chevron-up": <ChevronUp size={20} />,
  "circle-help": <CircleHelp size={20} />,
  cog: <Cog size={20} />,
  "drafting-compass": <DraftingCompass size={20} />,
  "external-link": <ExternalLink size={20} />,
  hand: <Hand size={20} />,
  "list-checks": <ListChecks size={20} />,
  "loader-circle": <LoaderCircle size={20} />,
  "log-out": <LogOut size={20} />,
  mail: <Mail size={20} />,
  "map-pin": <MapPin size={20} />,
  meh: <Meh size={20} />,
  "message-circle": <MessageCircle size={20} />,
  minus: <Minus size={20} />,
  plus: <Plus size={20} />,
  "rotate-ccw": <RotateCcw size={20} />,
  "send-horizontal": <SendHorizonal size={20} />,
  settings: <Settings size={20} />,
  x: <X size={20} />,
};

const colorMap = {
  red: "text-destructiveText bg-interfaceWhite border-destructiveBorder hover:bg-destructiveBgHover data-[state=on]:bg-destructiveDefault data-[state=on]:text-interfaceWhite",
  green:
    "text-successText bg-interfaceWhite border-successBorder hover:bg-successBgHover data-[state=on]:bg-successDefault data-[state=on]:text-interfaceWhite",
  blue: "text-actionText bg-interfaceWhite border-actionBorder hover:bg-actionBgHover data-[state=on]:bg-actionDefault data-[state=on]:text-interfaceWhite",
  yellow:
    "text-warningText bg-interfaceWhite border-warningBorder hover:bg-warningBgHover data-[state=on]:bg-warningDefault data-[state=on]:text-warningText",
};

export interface MultiSelectStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function MultiSelectStep({
  disabled,
  message,
  showLoading,
}: MultiSelectStepProps) {
  const { captureEvent } = useCaptureEvent();

  const firstElementFocusedRef = useRef(false);

  const questionData = message.question_data as QuestionData;
  const questions = questionData.questions || [];

  const { channel } = useChannel();

  const { conversation } = useAccount();

  const [submitting, setSubmitting] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState<{
    [questionIndex: number]: Array<number>;
  }>(() => {
    return questions.reduce((acc, _, questionIndex) => {
      acc[questionIndex] = [];
      return acc;
    }, {});
  });
  const [freeText, setFreeText] = useState<{
    [questionIndex: number]: string | undefined;
  }>(() => {
    return questions.reduce((acc, _, questionIndex) => {
      acc[questionIndex] = undefined;
      return acc;
    }, {});
  });

  const shouldShowMap = !!(
    questionData.image_url && conversation?.address_data?.gps_latitude
  );

  const { data: nearObjectsResponse } = useGetObjectsNearAddress(
    {
      path: {
        address_id: conversation?.address_data?.id || "",
      },
    },
    {
      enabled: shouldShowMap,
    },
  );

  const {
    isPending: cancelOrCompleteApplicationLoading,
    mutate: cancelOrCompleteApplication,
  } = useCancelOrCompleteApplication({});

  const handleClickRestart = () => {
    console.log("<MultiChoiceStep> handleClickRestart()");

    cancelOrCompleteApplication();
  };

  const handleClickNext = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      let text = questions
        .map((question, questionIndex) => {
          if (freeText[questionIndex] !== undefined) {
            if (question.question_text && questions.length > 1) {
              return `${question.question_text} ${freeText[questionIndex]}`;
            }
            return freeText[questionIndex];
          }
          const choiceIndexes = selectedChoices[questionIndex] || [];
          const choices = question.choices || [];

          return choiceIndexes
            .map(
              (choiceIndex) =>
                `${choices[choiceIndex].value} ${choices[choiceIndex].label}`,
            )
            .join(", ");
        })
        .flat()
        // filter not empty
        .filter((entry) => !!entry)
        .join(",\n");

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text,
      });

      console.log("<MultiSelectWidget> handleClickNext()", messageDescriptor);
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const shouldRenderQuestion = (question: Question) => {
    if (!question.dependency) {
      return true;
    }

    if (Array.isArray(question.dependency)) {
      return question.dependency.some((dependency) => {
        return selectedChoices[
          dependency.depends_on_question ?? 9999
        ]?.includes(dependency.depends_on_choice ?? 9999);
      });
    }

    return selectedChoices[
      question.dependency?.depends_on_question ?? 9999
    ]?.includes(question.dependency?.depends_on_choice ?? 9999);
  };

  const renderMap = () => {
    return (
      <MapComponent
        latitude={conversation?.address_data?.gps_latitude ?? undefined}
        longitude={conversation?.address_data?.gps_longitude ?? undefined}
        addressBoundary={conversation?.address_data?.boundary ?? undefined}
        nearObjects={nearObjectsResponse?.data.near_objects ?? undefined}
        className="mb-3 max-h-[200px] min-h-[200px] w-full rounded bg-[#E3E2E1] md:mb-5 md:max-h-[350px] md:min-h-[350px]"
      />
    );
  };

  const renderTextArea = (question: Question, questionIndex: number) => {
    let autoFocus: boolean | undefined = undefined;
    if (questionIndex === 0 && !firstElementFocusedRef.current) {
      firstElementFocusedRef.current = true;
      autoFocus = true;
    }

    return (
      <Textarea
        autoFocus={autoFocus}
        className="mt-3"
        data-testid={`question-free-text-${questionIndex}`}
        disabled={submitting || showLoading || disabled}
        onChange={(event) => {
          setFreeText((prev) => {
            return {
              ...prev,
              [questionIndex]: event.target.value,
            };
          });
        }}
        placeholder={getText(question.text_field_placeholder || "")}
        tabIndex={0}
        value={freeText[questionIndex] || ""}
      />
    );
  };

  const renderTextInput = (question: Question, questionIndex: number) => {
    let autoFocus: boolean | undefined = undefined;
    if (questionIndex === 0 && !firstElementFocusedRef.current) {
      firstElementFocusedRef.current = true;
      autoFocus = true;
    }

    return (
      <Input
        autoFocus={autoFocus}
        className="mt-3"
        data-testid={`question-free-text-${questionIndex}`}
        disabled={submitting || showLoading || disabled}
        onChange={(event) => {
          setFreeText((prev) => {
            return {
              ...prev,
              [questionIndex]: event.target.value,
            };
          });
        }}
        placeholder={getText(question.text_field_placeholder || "")}
        type="text"
        value={freeText[questionIndex] || ""}
      />
    );
  };

  const renderQuestion = (question: Question, questionIndex: number) => {
    const shouldRenderTextField = (question.text_field_lines || 0) > 0;

    const shouldRender = shouldRenderQuestion(question);
    if (!shouldRender) {
      return null;
    }

    return (
      <div key={questionIndex}>
        {question.question_text ? (
          <h3 className="text-textPrimary text-bodylg">
            {createMDWrapper(getText(question.question_text), {
              components: {
                h1({ children }) {
                  return <>{children}</>;
                },
                h2({ children }) {
                  return <>{children}</>;
                },
                h3({ children }) {
                  return <>{children}</>;
                },
                h4({ children }) {
                  return <>{children}</>;
                },
                h5({ children }) {
                  return <>{children}</>;
                },
                h6({ children }) {
                  return <>{children}</>;
                },
                p({ children }) {
                  return <div>{children}</div>;
                },
              },
            })}
          </h3>
        ) : null}

        {question.question_subtitle ? (
          <div className="text-textSubdued text-bodylg">
            {createMDWrapper(getText(question.question_subtitle), {
              components: {
                h1({ children }) {
                  return <>{children}</>;
                },
                h2({ children }) {
                  return <>{children}</>;
                },
                h3({ children }) {
                  return <>{children}</>;
                },
                h4({ children }) {
                  return <>{children}</>;
                },
                h5({ children }) {
                  return <>{children}</>;
                },
                h6({ children }) {
                  return <>{children}</>;
                },
                p({ children }) {
                  return <p>{children}</p>;
                },
              },
            })}
          </div>
        ) : null}

        <div className="mb-3" />

        {(question.choices || []).length > 0 ? (
          <ul
            className="flex flex-wrap gap-1"
            data-testid={`question-choices-${questionIndex}`}
          >
            {(question.choices || []).map((choice, choiceIndex) => {
              let autoFocus: boolean | undefined = undefined;
              if (
                questionIndex === 0 &&
                choiceIndex === 0 &&
                !firstElementFocusedRef.current
              ) {
                firstElementFocusedRef.current = true;
                autoFocus = true;
              }

              return (
                <li key={choiceIndex}>
                  <Toggle
                    autoFocus={autoFocus}
                    className={cn(choice.color && colorMap[choice.color])}
                    data-testid={`question-choice-${choiceIndex}`}
                    disabled={submitting || showLoading || disabled}
                    onPressedChange={(pressed) => {
                      if (question.allow_multiple) {
                        setSelectedChoices((prev) => {
                          if (pressed) {
                            return {
                              ...prev,
                              [questionIndex]: Array.from(
                                new Set([...prev[questionIndex], choiceIndex]),
                              ),
                            };
                          } else {
                            return {
                              ...prev,
                              [questionIndex]: prev[questionIndex].filter(
                                (ci) => ci !== choiceIndex,
                              ),
                            };
                          }
                        });
                      } else {
                        setSelectedChoices((prev) => {
                          if (pressed) {
                            return {
                              ...prev,
                              [questionIndex]: [choiceIndex],
                            };
                          } else {
                            return { ...prev, [questionIndex]: [] };
                          }
                        });
                      }
                    }}
                    pressed={selectedChoices[questionIndex].includes(
                      choiceIndex,
                    )}
                    variant={question.allow_multiple ? "rounded" : "outline"}
                  >
                    {choice.icon ? (
                      <div className="mr-1 [&>*]:h-4 [&>*]:w-4">
                        {iconMap[choice.icon]}
                      </div>
                    ) : null}

                    {sentenceCase(getText(choice.label))}
                  </Toggle>
                </li>
              );
            })}
          </ul>
        ) : null}

        {shouldRenderTextField && (question.text_field_lines ?? 1) > 1
          ? renderTextArea(question, questionIndex)
          : null}

        {shouldRenderTextField && (question.text_field_lines ?? 1) === 1
          ? renderTextInput(question, questionIndex)
          : null}
      </div>
    );
  };

  const renderFooter = () => {
    const isInvalid =
      !!questionData.required &&
      !Object.entries(selectedChoices).every(([questionIndex, value]) => {
        const question = questions[questionIndex];
        if (!shouldRenderQuestion(question)) return true;

        if (question.dependency) {
          if (Array.isArray(questions[questionIndex].dependency)) {
            return (
              question.dependency.every((dependency) => {
                return (
                  selectedChoices[dependency.depends_on_question ?? 9999]
                    .length > 0
                );
              }) && value.length > 0
            );
          } else {
            return (
              selectedChoices[question.dependency?.depends_on_question ?? 9999]
                .length > 0 && value.length > 0
            );
          }
        } else {
          return value.length > 0;
        }
      }) &&
      !freeText;

    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end gap-2 md:mt-8"
      >
        {questionData.show_restart_button && (
          <Button
            variant="outline"
            data-testid="restart-button"
            disabled={
              cancelOrCompleteApplicationLoading || submitting || disabled
            }
            onClick={handleClickRestart}
            size="sm"
          >
            {cancelOrCompleteApplicationLoading ? (
              <>
                <Loader2 className="animate-spin" data-testid="restarting" />
              </>
            ) : (
              <RotateCcw />
            )}
            {getText("Restart")}
          </Button>
        )}

        <Button
          data-testid="next-button"
          disabled={submitting || disabled || isInvalid}
          onClick={handleClickNext}
          size="sm"
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(questionData.next_button_label || "Next")}
          <ArrowRight className="ml-2" size={16} />
        </Button>
      </motion.div>
    );
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <div
      className="wizard-step-transition text-bodylg flex h-full w-full flex-col md:max-w-[600px]"
      data-testid="step-container-multi-select"
    >
      {shouldShowMap ? renderMap() : null}

      {questionData.question_title ? (
        <h2
          className={cn(
            "text-textPrimary",
            !questionData.disable_question_title_styling && "text-headingxl",
          )}
        >
          {createMDWrapper(getText(questionData.question_title), {
            components: {
              ...(!questionData.disable_question_title_styling
                ? {
                    h1({ children }) {
                      return <>{children}</>;
                    },
                    h2({ children }) {
                      return <>{children}</>;
                    },
                    h3({ children }) {
                      return <>{children}</>;
                    },
                    h4({ children }) {
                      return <>{children}</>;
                    },
                    h5({ children }) {
                      return <>{children}</>;
                    },
                    h6({ children }) {
                      return <>{children}</>;
                    },
                    p({ children }) {
                      return <div>{children}</div>;
                    },
                  }
                : {}),
            },
          })}
        </h2>
      ) : null}

      {questionData.question_subtitle ? (
        <div className="text-textSubdued text-bodylg">
          {createMDWrapper(getText(questionData.question_subtitle), {
            components: {
              h1({ children }) {
                return <>{children}</>;
              },
              h2({ children }) {
                return <>{children}</>;
              },
              h3({ children }) {
                return <>{children}</>;
              },
              h4({ children }) {
                return <>{children}</>;
              },
              h5({ children }) {
                return <>{children}</>;
              },
              h6({ children }) {
                return <>{children}</>;
              },
              p({ children }) {
                return (
                  <p className="text-textSubdued text-bodylg">{children}</p>
                );
              },
            },
          })}
        </div>
      ) : null}

      {questionData.question_title || questionData.question_subtitle ? (
        <div className="mb-3" />
      ) : null}

      <div
        className={cn(
          questions.length > 1
            ? "border-interactiveBorder rounded-lg border p-4"
            : "",
        )}
      >
        {questions.length > 0 ? (
          <div className="flex flex-col gap-3 md:gap-6">
            {questions.map(renderQuestion)}
          </div>
        ) : null}

        {renderFooter()}
      </div>
    </div>
  );
}
